/**
 * TrainingProgressBanner — always-on "what is the system doing right now" strip.
 *
 * Polls /api/v2/public/progress every ~8 seconds (cheap cached endpoint)
 * and renders a narrow top-of-layout strip that tells the user:
 *
 *   - Whether training is in progress (with ETA)
 *   - How many assets are production-ready vs awaiting first training
 *   - Whether the orchestrator needs manual intervention
 *   - Deployment mode (HUB / SPOKE) so users know feature gating applies
 *
 * Design rules:
 *   - Never blocks the UI (polling only; no modal)
 *   - Silent 0-height while idle + everything healthy + no job running
 *   - Compact colour-coded band during training / attention states
 *   - One-line message + optional progress bar + optional ETA
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Server,
  Clock,
  Radio,
} from 'lucide-react'
import wsManager from '@/services/websocket'

type Phase =
  | 'bootstrap'
  | 'awaiting_data'
  | 'kb_ready_no_models'
  | 'queued'
  | 'training'
  | 'ready'
  | 'attention_needed'
  | 'unknown'

interface ProgressPayload {
  timestamp: number
  phase: Phase
  overall_progress_pct: number | null
  eta_seconds: number | null
  message: string
  training: {
    available: boolean
    status: 'idle' | 'running' | 'unknown'
    pending: number
    running: number
    completed: number
    failed: number
    running_jobs: Array<{
      job_id?: string | null
      industry_id?: string | null
      asset_id?: string | null
      progress_pct?: number | null
      eta_seconds?: number | null
    }>
  }
  orchestrator: {
    healthy: boolean
    restart_count: number
    intervention_required: boolean
  }
  quality: {
    total_assets: number
    production_ready: number
    training_in_progress: number
    awaiting_training: number
    /** #57 quarantine: assets stuck on same data >= TRAINING_STUCK_THRESHOLD failures. */
    quarantined?: number
    /** #59: assets where prediction_stability < threshold; SME-recommended uncertainty review. */
    mc_dropout_advised?: number
  }
  llm?: {
    provider?: string | null
    model?: string | null
    reachable?: boolean | null
    base_url?: string | null
  }
  deployment: {
    mode: string
    is_hub: boolean
    is_spoke: boolean
    app_version: string
  }
  blocking: false
}

const PHASE_STYLE: Record<Phase, { bg: string; text: string; border: string; Icon: React.FC<any> }> = {
  bootstrap: {
    bg: 'bg-slate-800',    text: 'text-slate-200',  border: 'border-slate-700',
    Icon: Loader2,
  },
  awaiting_data: {
    bg: 'bg-amber-900/40', text: 'text-amber-200',  border: 'border-amber-700/60',
    Icon: AlertTriangle,
  },
  kb_ready_no_models: {
    bg: 'bg-amber-900/40', text: 'text-amber-200',  border: 'border-amber-700/60',
    Icon: AlertTriangle,
  },
  queued: {
    bg: 'bg-blue-900/40',  text: 'text-blue-200',   border: 'border-blue-700/60',
    Icon: Clock,
  },
  training: {
    bg: 'bg-blue-900/50',  text: 'text-blue-100',   border: 'border-blue-600/70',
    Icon: Activity,
  },
  ready: {
    bg: 'bg-emerald-900/40', text: 'text-emerald-200', border: 'border-emerald-700/60',
    Icon: CheckCircle2,
  },
  attention_needed: {
    bg: 'bg-red-900/50',   text: 'text-red-100',    border: 'border-red-600/70',
    Icon: AlertTriangle,
  },
  unknown: {
    bg: 'bg-slate-800',    text: 'text-slate-200',  border: 'border-slate-700',
    Icon: Server,
  },
}

function formatEta(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `~${h}h ${m}m remaining`
  if (m > 0) return `~${m}m remaining`
  return `~${seconds}s remaining`
}

const TrainingProgressBanner: React.FC = () => {
  const [data, setData] = useState<ProgressPayload | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [llmAlert, setLlmAlert] = useState<{ dropped_at: number; base_url?: string } | null>(null)
  const [wsLive, setWsLive] = useState(false)
  // Session-expired alert (May 4 2026): listen for the ryedore:session-expired
  // event emitted from api.ts after 3+ consecutive 401s. This is what
  // causes the [SIMULATED] / (Cached) labels to show — auth is dead and
  // every protected fetch is being denied.
  const [sessionAlert, setSessionAlert] = useState(false)
  useEffect(() => {
    const onSessionExpired = () => setSessionAlert(true)
    window.addEventListener('ryedore:session-expired', onSessionExpired)
    return () => window.removeEventListener('ryedore:session-expired', onSessionExpired)
  }, [])
  const intervalRef = useRef<number | null>(null)
  const prevReachableRef = useRef<boolean | null>(null)
  // Last WebSocket frame timestamp — used to know if `data` is being kept
  // fresh by WS pushes (sub-second) vs only by HTTP poll (3-8s).
  const lastWsFrameRef = useRef<number>(0)

  useEffect(() => {
    let cancelled = false
    const fetchProgress = async () => {
      try {
        const res = await fetch('/api/v2/public/progress', {
          credentials: 'include',
          cache: 'no-store',
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const payload: ProgressPayload = await res.json()
        if (cancelled) return
        setData(payload)

        // Mid-session drop detection: fire an alert on the first frame where
        // reachable transitions true → false. Avoids alerting on initial page
        // load when reachable starts as null/false.
        const prev = prevReachableRef.current
        const curr = payload.llm?.reachable ?? null
        if (prev === true && curr === false) {
          setLlmAlert({
            dropped_at: Date.now(),
            base_url: payload.llm?.base_url ?? undefined,
          })
        } else if (curr === true && llmAlert) {
          // Auto-clear alert once AI engine recovers
          setLlmAlert(null)
        }
        prevReachableRef.current = curr
      } catch {
        // Silent fail — banner shows last-known state or hides
      }
    }
    fetchProgress()
    // Poll cadence: 3s while training runs (sub-fixes the case where WS is
    // unavailable / unauthenticated); 8s when idle. WS pushes (below) shave
    // this further to <1s when connected. The poll is the always-on fallback.
    const activePoll = data?.training?.running && data.training.running > 0
    const pollMs = activePoll ? 3000 : 8000
    intervalRef.current = window.setInterval(fetchProgress, pollMs)
    return () => {
      cancelled = true
      if (intervalRef.current) window.clearInterval(intervalRef.current)
    }
  }, [llmAlert, data?.training?.running])

  // ─── WebSocket subscription — sub-second live updates ────────────────────
  // Subscribes to /api/v2/ws/training (mounted by app.py:4195). The backend
  // broadcasts queue_status (running/pending/completed counts) on connect +
  // job_progress / job_status on every training step. Updates are merged
  // into the same `data` state the HTTP poll fills, so React only re-renders
  // when something actually changed.
  //
  // Auth: wsManager pulls localStorage.auth_token and appends ?token=...
  // (server requires it; close codes 4001/4003 are handled inside wsManager
  // — no reconnect storm). When WS is down we fall back to the 3-8s poll
  // above silently, so the banner is always functional.
  useEffect(() => {
    let stopped = false
    const endpoint = '/ws/training'

    const onOpen = () => { if (!stopped) setWsLive(true) }
    const onClose = () => { if (!stopped) setWsLive(false) }

    const onMessage = (msg: unknown) => {
      if (!msg || typeof msg !== 'object' || stopped) return
      const m = msg as { type?: string; data?: any; job_id?: string;
                        progress?: number; step?: string; metrics?: any;
                        status?: string; running?: number; pending?: number;
                        completed?: number }
      lastWsFrameRef.current = Date.now()

      // queue_status (sent on connect + on every queue mutation)
      if (m.type === 'queue_status' && m.data) {
        setData(prev => prev ? {
          ...prev,
          training: {
            ...prev.training,
            running: m.data.running ?? prev.training.running,
            pending: m.data.pending ?? prev.training.pending,
            completed: m.data.completed ?? prev.training.completed,
            status: (m.data.running > 0 ? 'running' : 'idle') as 'running' | 'idle',
          },
          // Phase derives from running counts — keep it in sync with the
          // poll's logic so the icon/color match the WS-fresh data.
          phase: m.data.running > 0 ? 'training'
               : m.data.pending > 0 ? 'queued'
               : prev.phase,
        } : prev)
      }

      // job_progress — per-step updates with epoch + progress %
      else if (m.type === 'job_progress' && m.job_id) {
        setData(prev => {
          if (!prev) return prev
          const jobs = prev.training.running_jobs ?? []
          const updatedJobs = jobs.map(j =>
            j.job_id === m.job_id ? {
              ...j,
              progress_pct: m.progress ?? j.progress_pct,
              eta_seconds: m.metrics?.eta_seconds ?? j.eta_seconds,
            } : j
          )
          return {
            ...prev,
            training: { ...prev.training, running_jobs: updatedJobs },
            overall_progress_pct: m.progress ?? prev.overall_progress_pct,
            eta_seconds: m.metrics?.eta_seconds ?? prev.eta_seconds,
          }
        })
      }

      // job_status (started/completed/failed) — terminal transitions
      else if (m.type === 'job_status' && m.job_id) {
        if (m.status === 'completed' || m.status === 'failed') {
          // Force a poll refresh so we get the post-terminal counts (the
          // WS only sends the transition; counts come from REST).
          setTimeout(() => {
            fetch('/api/v2/public/progress', { credentials: 'include', cache: 'no-store' })
              .then(r => r.ok ? r.json() : null)
              .then(payload => { if (payload && !stopped) setData(payload) })
              .catch(() => {})
          }, 250)
        }
      }
    }

    wsManager.connect(endpoint, { onOpen, onClose, maxReconnectAttempts: 20 })
    const unsubscribe = wsManager.subscribe(endpoint, onMessage)

    return () => {
      stopped = true
      unsubscribe()
      // Don't disconnect — wsManager is a singleton; another subscriber
      // (RetrainingStatusPage) may still want it. Just unsubscribe.
    }
  }, [])

  // Hide banner only when healthy/ready AND there's nothing in-flight AND user dismissed
  const hideWhenClean = useMemo(() => {
    if (!data) return true  // hidden while first fetch in flight
    if (dismissed && data.phase === 'ready') return true
    return false
  }, [data, dismissed])

  if (!data && !llmAlert) return null
  // The LLM-drop alert overrides the dismissed/clean rule so users see it
  // even when everything else was healthy.
  const showMainBanner = data && !hideWhenClean

  const style = data ? (PHASE_STYLE[data.phase] ?? PHASE_STYLE.unknown) : PHASE_STYLE.unknown
  const { Icon } = style
  const eta = data ? formatEta(data.eta_seconds) : null

  return (
    <>
      {/* Session-expired alert (May 4 2026): fires after 3+ consecutive
          401s in any 30s window. Tells the user *why* the dashboard is
          showing [SIMULATED] / (Cached) data — their session token has
          expired and protected endpoints are being rejected. */}
      {sessionAlert && (
        <div
          className="flex items-center gap-3 border-b border-amber-700/60 bg-amber-900/50 text-amber-100 px-4 py-2 text-sm"
          role="alert"
        >
          <AlertTriangle size={16} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">Session expired or unauthenticated.</span>{' '}
            <span className="opacity-80">
              The dashboard is showing cached/sample data because your session token isn't valid.
              Sign in again to load live data.
            </span>
          </div>
          <a
            href="/login"
            className="text-xs font-medium underline opacity-90 hover:opacity-100 px-2"
          >
            Sign in
          </a>
          <button
            onClick={() => setSessionAlert(false)}
            className="text-xs opacity-80 hover:opacity-100 px-2"
            aria-label="Dismiss alert"
          >
            ×
          </button>
        </div>
      )}

      {/* Mid-session LLM-drop alert — fires the instant reachable goes
          true→false. Auto-clears when the engine recovers. */}
      {llmAlert && (
        <div
          className="flex items-center gap-3 border-b border-red-700/60 bg-red-900/50 text-red-100 px-4 py-2 text-sm"
          role="alert"
        >
          <AlertTriangle size={16} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">AI engine went offline.</span>{' '}
            <span className="opacity-80">
              {llmAlert.base_url ? `Check that the local AI service at ${llmAlert.base_url} is running.` : 'Check the local AI service.'}
              {' '}Chat, Oracle, and reasoning features are unavailable until it reconnects.
            </span>
          </div>
          <button
            onClick={() => setLlmAlert(null)}
            className="text-xs opacity-80 hover:opacity-100 px-2"
            aria-label="Dismiss alert"
          >
            ×
          </button>
        </div>
      )}

      {showMainBanner && data && (
    <div
      className={`flex items-center gap-3 border-b ${style.border} ${style.bg} ${style.text} px-4 py-2 text-sm`}
      role="status"
      aria-live="polite"
    >
      <Icon
        size={16}
        className={data.phase === 'training' || data.phase === 'bootstrap' ? 'animate-spin' : ''}
      />

      <div className="flex-1 min-w-0 flex items-center gap-3">
        <span className="truncate">{data.message}</span>
        {data.overall_progress_pct != null && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-28 h-1.5 rounded-full bg-black/30 overflow-hidden">
              <div
                className="h-full bg-current opacity-80 transition-all"
                style={{ width: `${Math.max(0, Math.min(100, data.overall_progress_pct))}%` }}
              />
            </div>
            <span className="text-xs tabular-nums">
              {data.overall_progress_pct.toFixed(0)}%
            </span>
          </div>
        )}
        {eta && (
          <span className="text-xs opacity-80 flex-shrink-0">{eta}</span>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs opacity-90 flex-shrink-0">
        <span>
          {data.quality.production_ready}/{data.quality.total_assets} ready
        </span>
        {/* Quarantine + MC-dropout advisories — only render when nonzero so
            the banner stays clean on healthy fleets. (Pass 3.4, May 4 2026) */}
        {data.quality.quarantined != null && data.quality.quarantined > 0 && (
          <span
            className="text-amber-300"
            title={`${data.quality.quarantined} asset(s) stuck on same data after >= 3 failed retrains; awaiting new data or manual retrain`}
          >
            • {data.quality.quarantined} quarantined
          </span>
        )}
        {data.quality.mc_dropout_advised != null && data.quality.mc_dropout_advised > 0 && (
          <span
            className="text-primary-300"
            title={`${data.quality.mc_dropout_advised} asset(s) flagged for uncertainty review (low prediction_stability)`}
          >
            • {data.quality.mc_dropout_advised} uncertainty-flagged
          </span>
        )}
        {data.training.running > 0 && (
          <span>• {data.training.running} running</span>
        )}
        {data.training.pending > 0 && (
          <span>• {data.training.pending} queued</span>
        )}
        {/* WS-feed indicator — pulses green when /ws/training is delivering
            sub-second updates; absent when the banner is on poll-only
            fallback. Labelled "WS" (not "LIVE") so it doesn't visually
            collide with the Header's LIVE button — that one tracks the
            REST API connection, this one tracks the training WebSocket.
            Hover reveals the underlying transport so ops can confirm the
            WebSocket is actually wired (the 4h Spoke log showed 0 WS
            connections — this dot makes that visible). */}
        {wsLive && (
          <span
            className="inline-flex items-center gap-1"
            title="Sub-second training updates via /ws/training (WebSocket connected)"
          >
            <Radio size={10} className="text-emerald-400 animate-pulse" />
            <span className="opacity-70">WS</span>
          </span>
        )}
        {/* Live AI engine health dot — green when reachable, red when down.
            Hidden when probe hasn't run yet (reachable == null). */}
        {data.llm?.reachable != null && (
          <span
            className="inline-flex items-center gap-1"
            title={
              data.llm.reachable
                ? `AI engine online (${data.llm.model ?? data.llm.provider ?? 'unknown'})`
                : `AI engine unreachable at ${data.llm.base_url ?? 'configured URL'}`
            }
          >
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                data.llm.reachable ? 'bg-emerald-400' : 'bg-red-500'
              }`}
            />
            <span>AI</span>
          </span>
        )}
        <span className="uppercase tracking-wide rounded bg-black/25 px-1.5 py-0.5">
          {data.deployment.is_hub ? 'HUB' : data.deployment.is_spoke ? 'SPOKE' : data.deployment.mode}
        </span>
        {data.phase === 'ready' && (
          <button
            onClick={() => setDismissed(true)}
            className="text-xs opacity-80 hover:opacity-100"
            aria-label="Hide banner"
          >
            ×
          </button>
        )}
      </div>
    </div>
      )}
    </>
  )
}

export default TrainingProgressBanner
